import React from 'react';

interface ButtonProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

export default function Button({
  children,
  className = '',
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  ...props
}: ButtonProps) {
  // BRAND v3 "LEDGER": square, tracked-out caps. Every button in the deck
  // reads this way (BOOK COLLECTION / READ THE NOTE / SEE THE TREND), and
  // keeping the case here rather than in call-site copy means the 100-odd
  // existing labels come along without being rewritten.
  const baseClasses = 'rounded-xs font-medium uppercase tracking-[0.14em] transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  // `primary` is Night, not oxblood. The identity spends oxblood only on what
  // needs the member — the "needs you today" banner, the unread badge, the
  // active nav marker — so making it the ground of every ordinary Save/Submit
  // in the app would spend the one loud colour on furniture. Use `accent` for
  // the deliberately urgent case.
  const variantClasses = {
    primary: 'bg-night text-paper hover:bg-ink',
    secondary: 'bg-transparent text-ink border border-mineral hover:border-ink',
    accent: 'bg-accent text-paper hover:bg-oxblood',
    ghost: 'bg-transparent text-ink border border-ink hover:bg-ink hover:text-paper',
    destructive: 'bg-signal text-paper hover:bg-oxblood'
  };

  const sizeClasses = {
    sm: 'text-[11px] px-4 py-2',
    md: 'text-xs px-6 py-3',
    lg: 'text-sm px-8 py-4'
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      {...props}
    >
      {children}
    </button>
  );
} 